// Drives `data-app-ready` on <html> for scripts/design-snapshot.mjs
// (BIT-44, docs/design-ops/remaining-work.md §4.3). Mount exactly once,
// at the App root — see the mount-site comment in App.tsx for why not
// per-shell.
//
// Inert in production: nothing else in the app reads this attribute.
// Never wire it to aria-busy — useIsFetching()/useIsMutating() count
// every observer app-wide, including Sidebar's and CommandPalette's
// persistently-mounted queries, so it would fire far more often than any
// real state change and would not be a usable status message (WCAG
// SC 4.1.3 wants status messages announced sparingly, not on every
// background refetch).
//
// WHY A QUIESCENCE WINDOW, NOT AN INSTANT CHECK
//
// The first version of this hook declared ready the instant
// useIsFetching() + useIsMutating() both read 0, gated only by a
// double-requestAnimationFrame boot delay. That closes the ambiguity at
// mount (0 in-flight because nothing has started vs. 0 because
// everything finished) but not a second, more common one: Shell.tsx
// gates useFacilityDashboard() on the recipe query's resolved value
// (`useFacilityDashboard(isFacilityDashboardEnabled(recipe))`). When the
// recipe query settles, React schedules a render; in that render
// useIsFetching() can read 0 because the newly-enabled dependent
// observer has been constructed but has not subscribed yet — TanStack
// Query starts the actual fetch from the observer's mount effect, which
// runs post-commit. A hook that trusts an instant 0 can commit
// data-app-ready="true" in that one-render gap, over a skeleton UI,
// before flipping back to "false" on the very next render. That is
// worse than the PWA's imperative signal it replaces: a forgotten
// `.set(true)` there fails loudly and identically every run (a 30s
// selector timeout); this failure mode succeeds every run while writing
// a wrong artifact under a green checkmark — the exact BIT-69 class this
// pipeline exists to prevent.
//
// The fix: don't trust a single "not busy" reading. Require it to hold
// for QUIET_FRAMES consecutive animation frames, and reset the counter
// the instant busy flips true. A dependent query's fetch starts from a
// post-commit effect — same tick or the next microtask — so it reliably
// flips `busy` back to true well inside a ~48ms (3-frame) window, long
// before the counter could reach threshold. 48ms is negligible against
// the snapshotter's 30s wait budget.
import { useIsFetching, useIsMutating } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useHoldCount } from "../loading/holdRegistry";

const QUIET_FRAMES = 3;

export function useAppReadySignal(): void {
  const isFetching = useIsFetching();
  const isMutating = useIsMutating();
  // A skeleton in its minimum-visible hold is still a loading screen,
  // even though no query is fetching any more (lib/loading/holdRegistry).
  const holds = useHoldCount();
  const busy = isFetching > 0 || isMutating > 0 || holds > 0;

  const quietFrames = useRef(0);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (busy) {
      quietFrames.current = 0;
      setReady(false);
      return;
    }

    let cancelled = false;
    let frameId = 0;
    const tick = () => {
      if (cancelled) return;
      quietFrames.current += 1;
      if (quietFrames.current >= QUIET_FRAMES) {
        setReady(true);
        return;
      }
      frameId = requestAnimationFrame(tick);
    };
    frameId = requestAnimationFrame(tick);

    return () => {
      cancelled = true;
      cancelAnimationFrame(frameId);
    };
  }, [busy]);

  useEffect(() => {
    document.documentElement.dataset.appReady = String(ready);
  }, [ready]);
}
